import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Save, X, Users } from 'lucide-react';

interface TeamMember { id: string; name: string; role: string; title: string; bio: string; imageUrl: string; email: string; phone: string; createdAt: string; }
interface Props { villageId: string; villageName: string; }

const ROLES = [
  { value: 'COMMUNITY_ADMIN', label: '👑 Community Admin' },
  { value: 'SECRETARY',       label: '📋 Secretary' },
  { value: 'TREASURER',       label: '💰 Treasurer' },
  { value: 'PRO',             label: '📣 PRO' },
  { value: 'CULTURAL_LEAD',   label: '🎭 Cultural Lead' },
  { value: 'YOUTH_LEAD',      label: '🌟 Youth Lead' },
  { value: 'WOMEN_LEAD',      label: '🌺 Women Lead' },
  { value: 'MEMBER',          label: '👤 Member' },
];
const EMPTY_MEMBER = { name: '', role: 'MEMBER', title: '', bio: '', imageUrl: '', email: '', phone: '' };

const CommunityTeamManager: React.FC<Props> = ({ villageId, villageName }) => {
  const [members, setMembers]   = useState<TeamMember[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState<string | null>(null);
  const [form, setForm]         = useState({ ...EMPTY_MEMBER });
  const [error, setError]       = useState('');

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const r = await fetch(`/api/community-portals/${villageId}/team`);
      const d = await r.json();
      if (d.success) setMembers(d.members || []);
    } catch { setError('Failed to load team'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchMembers(); }, [villageId]);

  const openNew = () => { setEditId(null); setForm({ ...EMPTY_MEMBER }); setShowForm(true); setError(''); };
  const openEdit = (m: TeamMember) => {
    setEditId(m.id);
    setForm({ name: m.name, role: m.role, title: m.title, bio: m.bio, imageUrl: m.imageUrl, email: m.email, phone: m.phone });
    setShowForm(true); setError('');
  };
  const cancel = () => { setShowForm(false); setEditId(null); setError(''); };

  const save = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      const url    = editId ? `/api/community-portals/${villageId}/team/${editId}` : `/api/community-portals/${villageId}/team`;
      const method = editId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await r.json();
      if (d.success) { await fetchMembers(); cancel(); } else setError(d.error || 'Save failed');
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const deleteMember = async (id: string, name: string) => {
    if (!confirm(`Remove ${name} from the team directory?`)) return;
    try {
      const r = await fetch(`/api/community-portals/${villageId}/team/${id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) await fetchMembers(); else setError(d.error || 'Delete failed');
    } catch { setError('Network error'); }
  };

  const roleLabel = (v: string) => ROLES.find(r => r.value === v)?.label ?? v;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Team Directory</h3>
          <p className="text-sm text-slate-400">{villageName} · {members.length} member{members.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#D4AF37] text-slate-900 hover:bg-[#c49b2f] transition-colors">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">{error}</div>}

      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-[#D4AF37]">{editId ? '✏️ Edit Member' : '➕ Add Team Member'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Full Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Full name..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Title / Position</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. LOC Chairman, Festival Coordinator..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Photo URL (optional)</label>
              <input value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                placeholder="https://..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Email (optional)</label>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="member@example.com"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Phone (optional)</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+234..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-slate-300 mb-1 block">Bio (optional)</label>
              <textarea rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                placeholder="Brief bio or description..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#0F5132] text-white hover:bg-[#0d4429] disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Member'}
            </button>
            <button onClick={cancel}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-8 text-center">Loading team…</div>
      ) : members.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl">
          <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No team members yet. Add the first member.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {members.map(m => (
            <div key={m.id}
              className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 flex items-start gap-3 hover:border-slate-600 transition-colors">
              {m.imageUrl ? (
                <img src={m.imageUrl} alt={m.name}
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-slate-600" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 text-[#D4AF37] font-bold text-lg">
                  {m.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">{m.name}</p>
                <p className="text-[#D4AF37] text-xs">{roleLabel(m.role)}</p>
                {m.title && <p className="text-slate-400 text-xs mt-0.5">{m.title}</p>}
                {m.email && <p className="text-slate-500 text-xs mt-0.5">{m.email}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => openEdit(m)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-700 transition-colors">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => deleteMember(m.id, m.name)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default CommunityTeamManager;
